<template>
  <div class="sci-view-container">
    <!-- Header -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button @click.prevent="home" class="sci-btn-back" title="Torna al Gestionale">
          <i class="fas fa-arrow-left"></i>
          <span>Gestionale</span>
        </button>
        <div class="sci-title-group">
          <div class="sci-badge-tag">
            <i class="fas fa-print"></i> Cassa & Fiscale
          </div>
          <h1 class="sci-view-title">Stampa Prenotati & Segnaposto</h1>
          <p class="sci-view-subtitle">Foglio di sala per il servizio, lista coperti e stampa cartoncini segnaposto</p>
        </div>
      </div>
      <div class="sci-header-actions">
        <button class="sci-btn sci-btn-primary" v-print="'#list'" title="Stampa lista completa prenotati">
          <i class="fas fa-print"></i>
          <span>Stampa Foglio Sala</span>
        </button>
        <button
          class="sci-btn sci-btn-accent"
          v-print="'#segnaposto'"
          :disabled="listToPrint.length === 0"
          title="Stampa segnaposto selezionati"
        >
          <i class="fas fa-tag"></i>
          <span>Stampa Segnaposto ({{ listToPrint.length }})</span>
        </button>
      </div>
    </header>

    <!-- Bento KPI Grid -->
    <section class="sci-kpi-grid">
      <div class="sci-kpi-card primary">
        <div class="kpi-icon-wrap">
          <i class="fas fa-calendar-check"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Prenotazioni Totali</span>
          <span class="kpi-value">{{ prenotazionisala.length }}</span>
          <span class="kpi-sub">{{ formattedSelectedDate }}</span>
        </div>
      </div>

      <div class="sci-kpi-card">
        <div class="kpi-icon-wrap blue">
          <i class="fas fa-users"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Coperti Previsti</span>
          <span class="kpi-value">{{ totaleCoperti }}</span>
          <span class="kpi-sub">Totale ospiti attesi</span>
        </div>
      </div>

      <div class="sci-kpi-card">
        <div class="kpi-icon-wrap orange">
          <i class="fas fa-tags"></i>
        </div>
        <div class="kpi-meta">
          <span class="kpi-label">Segnaposto Selezionati</span>
          <span class="kpi-value">{{ listToPrint.length }}</span>
          <span class="kpi-sub">Pronti per la stampa</span>
        </div>
      </div>
    </section>

    <!-- Controls Bar -->
    <section class="sci-card sci-controls-card">
      <div class="sci-controls-bar">
        <!-- Date Selector & Quick Controls -->
        <div class="sci-date-controls">
          <button class="sci-btn-icon" @click="changeDate(-1)" title="Giorno precedente">
            <i class="fas fa-chevron-left"></i>
          </button>
          <div class="sci-date-picker-wrap">
            <v-date-picker v-model="date" mode="date" :masks="{ input: 'YYYY-MM-DD' }">
              <template v-slot="{ inputValue, inputEvents }">
                <div class="sci-custom-date-input">
                  <i class="far fa-calendar-alt"></i>
                  <input :value="inputValue" v-on="inputEvents" class="date-raw-input" readonly />
                </div>
              </template>
            </v-date-picker>
          </div>
          <button class="sci-btn-icon" @click="changeDate(1)" title="Giorno successivo">
            <i class="fas fa-chevron-right"></i>
          </button>
          <button class="sci-preset-chip" @click="setToday">
            Oggi
          </button>
        </div>

        <!-- Segnaposto Quick Selection -->
        <div class="sci-selection-actions">
          <button class="sci-btn-sm" @click="selectAllSegnaposto" :disabled="prenotazionisala.length === 0">
            <i class="far fa-check-square"></i> Seleziona Tutti
          </button>
          <button class="sci-btn-sm" @click="clearSegnaposto" :disabled="listToPrint.length === 0">
            <i class="far fa-square"></i> Deseleziona
          </button>
        </div>
      </div>
    </section>

    <!-- Reservations Table (#list for vue3-print-nb) -->
    <section class="sci-card sci-table-card">
      <div class="table-container" id="list">
        <!-- Print Header (Visible in print) -->
        <div class="print-only print-doc-header">
          <h2>MesaPilot - Lista Prenotazioni di Sala</h2>
          <p>Data servizio: {{ formattedSelectedDate }} | Totale Prenotazioni: {{ prenotazionisala.length }} | Coperti: {{ totaleCoperti }}</p>
        </div>

        <table class="sci-table" v-if="prenotazionisala.length">
          <thead>
            <tr>
              <th>Ospite / Cognome</th>
              <th>Nome</th>
              <th>Coperti</th>
              <th>Orario</th>
              <th>Sala</th>
              <th>Tavolo</th>
              <th class="no-print text-center">Segnaposto</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="prenot in prenotazionisala" :key="prenot.tm + '_' + (prenot.numero || '')">
              <td class="guest-name">
                <strong>{{ prenot.cognome }}</strong>
              </td>
              <td>{{ prenot.nome }}</td>
              <td>
                <span class="sci-covers-badge">
                  <i class="fas fa-user-friends"></i> {{ prenot.coperti }}
                </span>
              </td>
              <td>
                <span class="sci-time-badge">
                  <i class="far fa-clock"></i> {{ formatTime(prenot.tm) }}
                </span>
              </td>
              <td>
                <span class="sci-room-badge">{{ prenot.codicesala || 'Sala Principale' }}</span>
              </td>
              <td>
                <span class="sci-table-badge">Tavolo {{ prenot.numero }}</span>
              </td>
              <td class="no-print text-center">
                <label class="sci-checkbox-container" title="Seleziona per cartoncino segnaposto">
                  <input
                    type="checkbox"
                    :checked="isSegnapostoSelected(prenot)"
                    @change="(e) => toggleSegnaposto(prenot, e)"
                  />
                  <span class="sci-checkmark"></span>
                </label>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Loading / Empty states -->
        <div v-else-if="loading" class="sci-state-feedback">
          <div class="sci-spinner"></div>
          <p class="state-title">Caricamento prenotazioni in corso...</p>
        </div>
        <div v-else class="sci-state-feedback">
          <div class="empty-icon-wrap">
            <i class="fas fa-calendar-times"></i>
          </div>
          <p class="state-title">Nessuna prenotazione trovata</p>
          <p class="state-subtitle">Non ci sono tavoli prenotati per la data selezionata.</p>
        </div>
      </div>
    </section>

    <!-- Segnaposto Container (#segnaposto for vue3-print-nb) -->
    <section class="sci-card sci-segnaposto-section" v-if="listToPrint.length">
      <div class="sci-section-header no-print">
        <div class="header-info">
          <h3><i class="fas fa-tag"></i> Anteprima Segnaposto ({{ listToPrint.length }})</h3>
          <p>I seguenti cartoncini verranno stampati quando premi "Stampa Segnaposto".</p>
        </div>
        <button class="sci-btn sci-btn-accent" v-print="'#segnaposto'">
          <i class="fas fa-print"></i> Stampa Cartoncini
        </button>
      </div>

      <div id="segnaposto" class="segnaposto-grid">
        <div class="segnaposto-card" v-for="listItem in listToPrint" :key="listItem.cognome + '_' + listItem.tavolo">
          <div class="card-border-decor">
            <div class="card-header print">
              <span class="decor-line"></span>
              <span class="header-text">TAVOLO RISERVATO</span>
              <span class="decor-line"></span>
            </div>
            <div class="card-body">
              <h2 class="card-title print">{{ listItem.cognome }}</h2>
              <div class="card-meta print">
                <span class="meta-item"><i class="fas fa-users"></i> {{ listItem.coperti }} Ospiti</span>
                <span class="meta-item-divider">•</span>
                <span class="meta-item table-highlight">Tavolo {{ listItem.tavolo }}</span>
              </div>
            </div>
            <div class="card-footer print">
              <small>MesaPilot Hospitality</small>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import moment from 'moment'
import { store } from '../store/store.js'

export default {
  name: 'StampaPrenotati',
  data() {
    return {
      date: new Date(),
      prenotazionisala: [],
      listToPrint: [],
      loading: false,
      store
    }
  },
  computed: {
    formattedSelectedDate() {
      return moment(this.date).format('DD/MM/YYYY');
    },
    totaleCoperti() {
      return this.prenotazionisala.reduce((sum, p) => sum + (Number(p.coperti) || 0), 0);
    }
  },
  watch: {
    date: {
      handler(val) {
        if (val) {
          this.displayPrenotazioni();
        }
      }
    }
  },
  methods: {
    home() {
      this.$router.push({ name: "Gestionale" });
    },
    getToken() {
      try {
        if (!localStorage.token) return '';
        const parsed = JSON.parse(localStorage.token);
        return typeof parsed === 'string' ? parsed : (parsed.token || localStorage.token);
      } catch (e) {
        return localStorage.token || '';
      }
    },
    changeDate(days) {
      const d = new Date(this.date);
      d.setDate(d.getDate() + days);
      this.date = d;
    },
    setToday() {
      this.date = new Date();
    },
    formatTime(tm) {
      if (!tm) return '-';
      return moment.unix(tm).format('HH:mm');
    },
    isSegnapostoSelected(prenot) {
      return this.listToPrint.some(item => item.cognome === prenot.cognome && item.tavolo === prenot.numero);
    },
    toggleSegnaposto(prenot, e) {
      if (e.target.checked) {
        if (!this.isSegnapostoSelected(prenot)) {
          this.listToPrint.push({
            cognome: prenot.cognome,
            coperti: prenot.coperti,
            tavolo: prenot.numero
          });
        }
      } else {
        const idx = this.listToPrint.findIndex(item => item.cognome === prenot.cognome && item.tavolo === prenot.numero);
        if (idx !== -1) {
          this.listToPrint.splice(idx, 1);
        }
      }
    },
    selectAllSegnaposto() {
      this.listToPrint = this.prenotazionisala.map(p => ({
        cognome: p.cognome,
        coperti: p.coperti,
        tavolo: p.numero
      }));
    },
    clearSegnaposto() {
      this.listToPrint = [];
    },
    async displayPrenotazioni() {
      this.loading = true;
      const end = moment(this.date).endOf('day').unix();
      const start = moment(this.date).startOf('day').unix();
      const token = this.getToken();

      try {
        const body = {
          end,
          start,
          codicesala: this.sala || '',
          stato: 'prenotato',
          tutte: true,
          headers: {
            authorization: `Bearer ${token}`
          }
        };
        const config = {
          headers: {
            authorization: `Bearer ${token}`
          }
        };

        const response = await this.axios.post(this.store.path + 'displayPrenotazioni', body, config);
        if (response.data && response.data.messaggio) {
          this.prenotazionisala = response.data.messaggio;
        } else {
          this.prenotazionisala = [];
        }
      } catch (err) {
        console.error("Errore recupero prenotazioni:", err);
        this.prenotazionisala = [];
      } finally {
        this.loading = false;
      }
    }
  },
  mounted() {
    this.displayPrenotazioni();
  }
}
</script>

<style scoped>
.sci-view-container {
  min-height: 100vh;
  background-color: #f1f5f9;
  padding: 24px 32px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #0f172a;
  box-sizing: border-box;
}

/* Header */
.sci-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  color: #334155;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.sci-btn-back:hover {
  background: #f8fafc;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateX(-2px);
}

.sci-badge-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #0284c7;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.sci-view-title {
  font-size: 1.65rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1.2;
}

.sci-view-subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 4px 0 0 0;
}

.sci-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* Buttons */
.sci-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.sci-btn-primary {
  background: #0284c7;
  color: #ffffff;
}

.sci-btn-primary:hover:not(:disabled) {
  background: #0369a1;
}

.sci-btn-accent {
  background: #d97706;
  color: #ffffff;
}

.sci-btn-accent:hover:not(:disabled) {
  background: #b45309;
}

.sci-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Bento KPI Grid */
.sci-kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.sci-kpi-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.sci-kpi-card.primary {
  border-left: 4px solid #0284c7;
}

.kpi-icon-wrap {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: #f0f9ff;
  color: #0284c7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  flex-shrink: 0;
}

.kpi-icon-wrap.blue {
  background: #eff6ff;
  color: #2563eb;
}

.kpi-icon-wrap.orange {
  background: #fffbeb;
  color: #d97706;
}

.kpi-meta {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
}

.kpi-value {
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
  margin: 2px 0;
}

.kpi-sub {
  font-size: 0.75rem;
  color: #94a3b8;
}

/* Card General */
.sci-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
  overflow: hidden;
}

/* Controls Bar */
.sci-controls-card {
  padding: 16px 20px;
}

.sci-controls-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.sci-date-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sci-btn-icon {
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
}

.sci-btn-icon:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.sci-custom-date-input {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 12px;
  color: #0f172a;
  font-weight: 600;
}

.date-raw-input {
  border: none;
  background: transparent;
  font-weight: 600;
  color: #0f172a;
  outline: none;
  width: 110px;
  cursor: pointer;
}

.sci-preset-chip {
  min-height: 38px;
  padding: 0 14px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
  transition: all 0.2s;
}

.sci-preset-chip:hover {
  background: #e2e8f0;
}

.sci-selection-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sci-btn-sm {
  min-height: 38px;
  padding: 0 12px;
  background: #f8fafc;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
}

.sci-btn-sm:hover:not(:disabled) {
  background: #e2e8f0;
  color: #0f172a;
}

.sci-btn-sm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Table */
.sci-table-card {
  padding: 0;
}

.table-container {
  overflow-x: auto;
}

.sci-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.sci-table th {
  background: #f8fafc;
  color: #475569;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
}

.sci-table td {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  font-size: 0.95rem;
  vertical-align: middle;
}

.sci-table tbody tr:hover {
  background: #f8fafc;
}

.guest-name {
  font-weight: 700;
  color: #0f172a;
}

.sci-covers-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f0fdf4;
  color: #16a34a;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 700;
}

.sci-time-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f8fafc;
  color: #475569;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 600;
}

.sci-room-badge {
  color: #64748b;
  font-weight: 500;
}

.sci-table-badge {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-weight: 700;
  background: #f1f5f9;
  color: #1e293b;
  padding: 4px 8px;
  border-radius: 6px;
}

/* Custom Checkbox */
.sci-checkbox-container {
  display: inline-flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  user-select: none;
  width: 24px;
  height: 24px;
}

.sci-checkbox-container input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  height: 0;
  width: 0;
}

.sci-checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 24px;
  width: 24px;
  background-color: #ffffff;
  border: 2px solid #cbd5e1;
  border-radius: 6px;
  transition: all 0.2s;
}

.sci-checkbox-container:hover input ~ .sci-checkmark {
  border-color: #d97706;
}

.sci-checkbox-container input:checked ~ .sci-checkmark {
  background-color: #d97706;
  border-color: #d97706;
}

.sci-checkmark:after {
  content: "";
  position: absolute;
  display: none;
}

.sci-checkbox-container input:checked ~ .sci-checkmark:after {
  display: block;
}

.sci-checkbox-container .sci-checkmark:after {
  left: 8px;
  top: 4px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

/* Segnaposto Section */
.sci-segnaposto-section {
  padding: 24px;
}

.sci-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.sci-section-header h3 {
  font-size: 1.2rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px 0;
}

.sci-section-header p {
  font-size: 0.85rem;
  color: #64748b;
  margin: 0;
}

.segnaposto-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.segnaposto-card {
  background: #ffffff;
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.card-border-decor {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  background: #fafaf9;
}

.card-header.print {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
}

.decor-line {
  flex: 1;
  height: 1px;
  background: #cbd5e1;
}

.header-text {
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 1.5px;
  color: #78716c;
}

.card-title.print {
  font-size: 1.4rem;
  font-weight: 800;
  color: #1c1917;
  margin: 0 0 8px 0;
  text-transform: uppercase;
}

.card-meta.print {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.9rem;
  color: #57534e;
  font-weight: 600;
}

.meta-item-divider {
  color: #a8a29e;
}

.table-highlight {
  color: #d97706;
  font-weight: 800;
}

.card-footer.print {
  margin-top: 12px;
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;
  font-size: 0.7rem;
  color: #a8a29e;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Feedback States */
.sci-state-feedback {
  padding: 60px 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.sci-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top-color: #0284c7;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-icon-wrap {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: #f1f5f9;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  margin-bottom: 16px;
}

.state-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px 0;
}

.state-subtitle {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0;
}

.print-only {
  display: none;
}

/* Print Specific Rules for vue3-print-nb */
@media print {
  body {
    background: #ffffff !important;
    color: #000000 !important;
  }

  .no-print, .sci-header, .sci-kpi-grid, .sci-controls-card {
    display: none !important;
  }

  .print-only {
    display: block !important;
  }

  .print-doc-header {
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 2px solid #000;
    text-align: center;
  }

  .print-doc-header h2 {
    font-size: 18pt;
    margin: 0 0 5px 0;
  }

  .print-doc-header p {
    font-size: 11pt;
    margin: 0;
    color: #444;
  }

  .sci-table {
    width: 100% !important;
    border-collapse: collapse !important;
  }

  .sci-table th, .sci-table td {
    border: 1px solid #999 !important;
    padding: 8px 10px !important;
    font-size: 10pt !important;
    color: #000 !important;
  }

  .sci-table th {
    background: #f0f0f0 !important;
  }

  /* Segnaposto print formatting */
  .segnaposto-grid {
    display: block !important;
  }

  .segnaposto-card {
    page-break-inside: avoid;
    break-inside: avoid;
    margin-bottom: 25px !important;
    border: 2px solid #000 !important;
    padding: 20px !important;
    background: #fff !important;
    box-shadow: none !important;
  }

  .card-border-decor {
    border: 1px solid #666 !important;
    background: #fff !important;
  }

  .print {
    text-align: center !important;
  }

  .card-title.print {
    font-size: 20pt !important;
  }

  .card-meta.print {
    font-size: 13pt !important;
  }
}
</style>
